import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import {
  adminLogin,
  participantSignIn,
  pickTrack,
  requestPasswordReset,
  resetPassword,
} from "../../api/auth";
import { fetchContent } from "../../api/content";
import type { Track } from "../../api/types";
import { AppLayout } from "../../components/AppLayout";
import { ScreenState } from "../../components/ScreenState";
import { attempt } from "../../lib/errors";
import { readJsonPref, writePref } from "../../lib/prefs";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { AdminLoginScreen } from "../../screens/auth/AdminLoginScreen";
import { ForgotPasswordScreen, ResetPasswordScreen } from "../../screens/auth/AdminPasswordScreens";
import { PickTrackScreen } from "../../screens/auth/PickTrackScreen";
import { SignInScreen, type SignInValues } from "../../screens/auth/SignInScreen";

/* Per-device convenience only: prefills the form next time. Progress lives on the server. */
const REMEMBER_KEY = "lastSignIn";

export function SignInRoute() {
  const { user, loading, setUser } = useSession();
  const navigate = useNavigate();
  const [remembered, setRemembered] = useState(() => readJsonPref<SignInValues>(REMEMBER_KEY));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && user)
    return <Navigate to={user.role === "Participant" ? "/hub" : "/admin/home"} replace />;

  return (
    <AppLayout>
      <ScreenState loading={loading} error={null}>
        <SignInScreen
          key={remembered ? "remembered" : "blank"}
          remembered={remembered}
          busy={busy}
          error={error}
          onForget={() => {
            writePref(REMEMBER_KEY, null);
            setRemembered(null);
          }}
          onSubmit={(values) =>
            void attempt(setBusy, setError, async () => {
              const signedIn = await participantSignIn(values);
              writePref(REMEMBER_KEY, JSON.stringify(values));
              setUser(signedIn);
              void navigate(signedIn.track ? "/hub" : "/track", { replace: true });
            })
          }
        />
      </ScreenState>
    </AppLayout>
  );
}

export function PickTrackRoute() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();
  const content = useAsync(fetchContent, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user?.track) return <Navigate to="/hub" replace />;

  return (
    <ScreenState loading={content.loading} error={content.error} onRetry={content.reload}>
      {content.data ? (
        <>
          <PickTrackScreen
            busy={busy}
            tracks={(Object.keys(content.data.tracks) as Track[]).map((key) => ({
              key,
              label: content.data!.tracks[key].label,
            }))}
            onPick={(track) =>
              void attempt(setBusy, setError, async () => {
                setUser(await pickTrack(track));
                void navigate("/hub", { replace: true });
              })
            }
          />
          {error ? (
            <p role="alert" className="mt-3 text-negative">
              {error}
            </p>
          ) : null}
        </>
      ) : null}
    </ScreenState>
  );
}

export function AdminLoginRoute() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user && user.role !== "Participant") return <Navigate to="/admin/home" replace />;

  return (
    <AppLayout>
      <AdminLoginScreen
        busy={busy}
        error={error}
        onSubmit={(email, password) =>
          void attempt(setBusy, setError, async () => {
            setUser(await adminLogin(email, password));
            void navigate("/admin/home", { replace: true });
          })
        }
      />
    </AppLayout>
  );
}

export function ForgotPasswordRoute() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  return (
    <AppLayout>
      <ForgotPasswordScreen
        busy={busy}
        error={error}
        sent={sent}
        onSubmit={(email) =>
          void attempt(setBusy, setError, async () => {
            await requestPasswordReset(email);
            setSent(true);
          })
        }
      />
    </AppLayout>
  );
}

export function ResetPasswordRoute() {
  const { setUser } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AppLayout>
      <ResetPasswordScreen
        hasToken={Boolean(token)}
        busy={busy}
        error={error}
        onSubmit={(newPassword) =>
          void attempt(setBusy, setError, async () => {
            setUser(await resetPassword(token, newPassword));
            void navigate("/admin/home", { replace: true });
          })
        }
      />
    </AppLayout>
  );
}
